import { useEffect, useState } from "react";
import { useUpdateGasStationBrand } from "@/lib/queries";
import { COUNTRIES } from "@/lib/countries";
import type { GasStationBrand } from "@/types/database";
import { Button } from "@/components/ui/Button";
import { Field, Input, Select } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";
import { imageSrc } from "@/lib/utils";

const ACCEPTED_LOGO_TYPES = "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";

function formFromBrand(brand: GasStationBrand) {
  return {
    brandName: brand.brandName ?? "",
    country: brand.country ?? "Jamaica",
    website: brand.website ?? "",
    phone: brand.phone != null ? String(brand.phone) : "",
    description: brand.description ?? "",
  };
}

function parsePhoneNumber(value: string): number | null {
  const digits = value.replace(/\D/g, "");
  if (!digits) return null;
  const phone = Number(digits);
  if (!Number.isFinite(phone)) return null;
  return phone;
}

export function EditGasStationBrandModal({
  brand,
  open,
  onClose,
}: {
  brand: GasStationBrand;
  open: boolean;
  onClose: () => void;
}) {
  const updateBrand = useUpdateGasStationBrand();
  const [form, setForm] = useState(() => formFromBrand(brand));
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const saving = updateBrand.isPending;
  const existingLogoUrl = imageSrc(brand.brandLogoUrl);

  useEffect(() => {
    if (!open) return;
    setForm(formFromBrand(brand));
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setError(null);
  }, [open, brand]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [logoFile]);

  function closeModal() {
    if (saving) return;
    onClose();
  }

  function handleLogoChange(file: File | null) {
    if (!file) {
      setLogoFile(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Logo must be an image file.");
      return;
    }

    setError(null);
    setLogoFile(file);
  }

  async function handleSave() {
    const brandName = form.brandName.trim();
    if (!brandName) {
      setError("Brand name is required.");
      return;
    }

    setError(null);
    try {
      await updateBrand.mutateAsync({
        id: brand.id,
        brandName,
        country: form.country || null,
        website: form.website.trim() || null,
        phone: parsePhoneNumber(form.phone),
        description: form.description.trim() || null,
        logoFile,
        brandLogoUrl: brand.brandLogoUrl,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update brand");
    }
  }

  const previewSrc = logoPreviewUrl ?? existingLogoUrl;

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title="Edit gas station brand"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="brand" onClick={handleSave} disabled={saving || !form.brandName.trim()}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      {error ? <p className="mb-4 rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p> : null}
      <div className="space-y-4 text-foreground">
        <Field label="Brand name">
          <Input
            value={form.brandName}
            onChange={(e) => setForm((current) => ({ ...current, brandName: e.target.value }))}
            placeholder="e.g. Rubis"
            autoFocus
          />
        </Field>

        <Field label="Country">
          <Select
            value={form.country}
            onChange={(e) => setForm((current) => ({ ...current, country: e.target.value }))}
          >
            <option value="">Select a country</option>
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Website (optional)">
            <Input
              type="text"
              value={form.website}
              onChange={(e) => setForm((current) => ({ ...current, website: e.target.value }))}
              placeholder="https://example.com"
            />
          </Field>
          <Field label="Phone (optional)">
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))}
              placeholder="e.g. 18765551234"
            />
          </Field>
        </div>

        <Field label="Brand logo">
          <div className="space-y-3">
            {previewSrc ? (
              <div className="flex h-24 items-center justify-center rounded-2xl bg-canvas p-3">
                <img src={previewSrc} alt="Logo preview" className="max-h-full max-w-full object-contain" />
              </div>
            ) : null}
            <Input
              type="file"
              accept={ACCEPTED_LOGO_TYPES}
              onChange={(e) => handleLogoChange(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted">
              Leave empty to keep the current logo. Upload a new file to replace it.
            </p>
          </div>
        </Field>

        <Field label="Description">
          <textarea
            value={form.description}
            onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
            placeholder="Optional notes about this brand"
            rows={3}
            className={cn("field-input min-h-[5.5rem] resize-y")}
          />
        </Field>
      </div>
    </Modal>
  );
}
